import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { approvalAPI } from '../../api/services';

interface Approval {
  _id: string;
  projectId: any;
  projectName: string;
  requestedBy: any;
  requestedByName: string;
  requestedDepartmentId: any;
  requestedDepartmentName: string;
  targetDepartmentId?: any;
  targetDepartmentName?: string;
  approvalType: string;
  approvalChain: ApprovalChainItem[];
  status: string;
  requestReason?: string;
  urgency: string;
  requestedAt: string;
  shareableLinkExpiresAt?: string;
}

interface ApprovalChainItem {
  approverId: string;
  approverName: string;
  approverRoleName: string;
  status: string;
  comments?: string;
  actionAt?: string;
}

const ApprovalByToken: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [approval, setApproval] = useState<Approval | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    if (token) {
      fetchApproval();
    }
  }, [token]);

  const fetchApproval = async () => {
    try {
      setLoading(true);
      const response = await approvalAPI.getApprovalByToken(token!);
      setApproval(response.data.data);
    } catch (err: any) {
      console.error('Error fetching approval:', err);
      setError(err.response?.data?.message || 'Failed to load approval request');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await approvalAPI.approveViaToken(token!, comments);
      setShowConfirm(false);
      fetchApproval(); // Refresh to show updated status
      alert('Collaboration request approved successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAction = (actionType: 'approve' | 'reject') => {
    setAction(actionType);
    setShowConfirm(true);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getUrgencyColor = (urgency: string) => {
    const colors: Record<string, string> = {
      low: 'text-gray-600',
      medium: 'text-blue-600',
      high: 'text-orange-600',
      critical: 'text-red-600',
    };
    return colors[urgency] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Approval</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <p className="text-gray-600">Approval request not found</p>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = approval.shareableLinkExpiresAt && new Date() > new Date(approval.shareableLinkExpiresAt);
  const canTakeAction = (approval.status === 'pending' || approval.status === 'in_progress') && !isExpired;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
            <h1 className="text-3xl font-bold mb-2">Collaboration Request</h1>
            <p className="text-blue-100">Review and respond to this approval request</p>
          </div>

          <div className="p-8">
            {/* Status Banner */}
            <div className="mb-6 flex items-center justify-between">
              <span className={`px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(approval.status)}`}>
                {approval.status.toUpperCase()}
              </span>
              {isExpired && (
                <span className="px-4 py-2 text-sm font-semibold rounded-full bg-gray-100 text-gray-700">
                  LINK EXPIRED
                </span>
              )}
            </div>

            {/* Project Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h2 className="text-sm font-medium text-blue-900 mb-2">Project</h2>
              <p className="text-2xl font-bold text-blue-700 mb-4">{approval.projectName}</p>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-blue-900 font-medium mb-1">Requesting Department:</p>
                  <p className="text-blue-700">{approval.requestedDepartmentName}</p>
                </div>
                <div>
                  <p className="text-blue-900 font-medium mb-1">Requested By:</p>
                  <p className="text-blue-700">{approval.requestedByName}</p>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div className="space-y-4 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Target Department:</h3>
                <p className="text-gray-700">{approval.targetDepartmentName || 'Not specified'}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Request Type:</h3>
                <p className="text-gray-700 capitalize">{approval.approvalType.replace('_', ' ')}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Urgency Level:</h3>
                <p className={`font-semibold ${getUrgencyColor(approval.urgency)}`}>
                  {approval.urgency.toUpperCase()}
                </p>
              </div>

              {approval.requestReason && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Reason for Collaboration:</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{approval.requestReason}</p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Requested On:</h3>
                <p className="text-gray-700">{new Date(approval.requestedAt).toLocaleString()}</p>
              </div>
            </div>

            {/* Approval Chain */}
            {approval.approvalChain.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Approval Chain:</h3>
                <div className="space-y-3">
                  {approval.approvalChain.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.approverName}</p>
                        <p className="text-sm text-gray-600">({item.approverRoleName})</p>
                        {item.comments && (
                          <p className="text-sm text-gray-700 mt-2 italic">"{item.comments}"</p>
                        )}
                        {item.actionAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(item.actionAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {canTakeAction && (
              <div className="border-t border-gray-200 pt-6">
                <div className="flex gap-4">
                  <button
                    onClick={() => confirmAction('reject')}
                    className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
                  >
                    Reject Request
                  </button>
                  <button
                    onClick={() => confirmAction('approve')}
                    className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
                  >
                    Approve Request
                  </button>
                </div>
              </div>
            )}

            {!canTakeAction && !isExpired && (
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-gray-600">This request has already been {approval.status}</p>
                </div>
              </div>
            )}

            {isExpired && (
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-yellow-800">This approval link has expired. Please contact the requester for a new link.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {action === 'approve' ? 'Approve' : 'Reject'} Collaboration Request
              </h2>
              <p className="text-gray-600 mb-4">
                {action === 'approve'
                  ? 'By approving, you agree to collaborate on this project. Your department members will be able to work on this project.'
                  : 'Please provide a reason for rejection to help the requester understand your decision.'
                }
              </p>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments {action === 'reject' && '(Required)'}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={action === 'approve' ? 'Optional comments...' : 'Reason for rejection...'}
                />
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowConfirm(false);
                    setAction(null);
                    setComments('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  className={`flex-1 px-6 py-3 ${
                    action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  } text-white font-semibold rounded-lg disabled:opacity-50`}
                  disabled={actionLoading || (action === 'reject' && !comments.trim())}
                >
                  {actionLoading ? 'Processing...' : `Confirm ${action === 'approve' ? 'Approval' : 'Rejection'}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalByToken;
